<template>
  <div class="space-y-6">
    <h1 class="text-3xl font-bold text-neutral-900 dark:text-neutral-100">HomeBase Collection Receipts</h1>

    <!-- Collection Receipts -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft p-6">
      <h2 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6">Collection Receipts</h2>
      <div class="flex flex-wrap items-center gap-4 mb-6">
        <select
          v-model="filters.status"
          aria-label="Filter by status"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        >
          <option value="any">All Status</option>
          <option value="requested">Requested</option>
          <option value="completed">Completed</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <input
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 transition-all duration-200"
          type="text"
          placeholder="Search Villa Number"
          aria-label="Search villa number"
          v-model="filters.householdId"
        />
        <input
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 transition-all duration-200"
          type="text"
          placeholder="Assigned To"
          aria-label="Filter by assigned user"
          v-model="filters.assignedTo"
        />
        <div class="flex items-center gap-2">
          <label for="receipts-date-from" class="text-sm font-medium text-neutral-700 dark:text-neutral-300">From</label>
          <input
            id="receipts-date-from"
            class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
            type="date"
            v-model="filters.dateFrom"
          />
        </div>
        <div class="flex items-center gap-2">
          <label for="receipts-date-to" class="text-sm font-medium text-neutral-700 dark:text-neutral-300">To</label>
          <input
            id="receipts-date-to"
            class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
            type="date"
            v-model="filters.dateTo"
          />
        </div>
        <button
          @click="reload"
          class="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          Apply Filters
        </button>
        <button
          @click="exportToCSV"
          :disabled="!collections.length"
          class="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Export CSV
        </button>
      </div>

      <AlertBanner
        v-if="error && collections.length"
        type="error"
        message="Couldn't refresh collection receipts — showing the last loaded list."
        :dismissible="false"
        class="mb-4"
      />
      <AlertBanner
        v-else-if="!loading && limitReached"
        type="info"
        :message="`Showing the first ${collections.length} results — narrow the date range for accurate totals.`"
        :dismissible="false"
        class="mb-4"
      />

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="flex flex-col items-center gap-3">
          <svg class="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-sm text-neutral-600 dark:text-neutral-400">Loading collection receipts...</p>
        </div>
      </div>

      <!-- Error State (nothing loaded yet) -->
      <ErrorState
        v-else-if="error && !collections.length"
        title="Couldn't load collection receipts"
        :message="error"
        @retry="reload"
      />

      <!-- Empty State -->
      <div v-else-if="!sortedAndFiltered.length" class="py-12 text-center">
        <div class="flex flex-col items-center gap-3">
          <span class="material-icons text-5xl text-neutral-400 dark:text-neutral-600" aria-hidden="true">inbox</span>
          <p class="text-neutral-600 dark:text-neutral-400">No collection receipts found</p>
        </div>
      </div>

      <!-- Data Table -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
            <tr>
              <th :aria-sort="ariaSort('requestedAt')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('requestedAt')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Date
                  <span v-if="sortState.key === 'requestedAt'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('householdVilla')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('householdVilla')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Villa - Community
                  <span v-if="sortState.key === 'householdVilla'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('containerSerial')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('containerSerial')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Container Serial
                  <span v-if="sortState.key === 'containerSerial'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('volumeL')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('volumeL')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Volume (L)
                  <span v-if="sortState.key === 'volumeL'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('performedBy')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('performedBy')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Performed By
                  <span v-if="sortState.key === 'performedBy'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('status')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('status')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Status
                  <span v-if="sortState.key === 'status'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <tr v-for="collection in sortedAndFiltered" :key="collection.id" class="hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors">
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ (collection.requestedAt || collection.createdAt || '').slice(0,10) }}</td>
              <td class="px-4 py-3 text-sm">
                <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  <template v-if="collection.householdVilla">
                    {{ collection.householdVilla }}<span v-if="collection.householdCommunity"> - {{ collection.householdCommunity }}</span>
                  </template>
                  <template v-else>
                    {{ collection.householdId }}
                  </template>
                </span>
              </td>
              <td class="px-4 py-3 text-sm">
                <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">{{ collection.containerSerial || collection.containerId }}</span>
              </td>
              <td class="px-4 py-3 text-sm">
                <span class="font-bold tabular-nums text-neutral-900 dark:text-neutral-100">{{ collection.volumeL || '-' }}</span>
              </td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ collection.performedBy || collection.assignedTo || '-' }}</td>
              <td class="px-4 py-3 text-sm">
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                  :class="{
                    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': collection.status === 'completed',
                    'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300': collection.status === 'delivered',
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300': collection.status === 'requested',
                    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300': collection.status === 'cancelled',
                    'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200': !['completed', 'delivered', 'requested', 'cancelled'].includes(collection.status)
                  }"
                >
                  {{ collection.status }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center gap-4 mt-6">
        <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
          Collections shown: {{ collections.length }}
        </span>
        <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
          Volume shown: {{ totalVolume }}L
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive } from 'vue'
import useHomebaseCollectionReceipts from '@/composables/useHomebaseCollectionReceipts'
import ErrorState from '@/components/ui/ErrorState.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'

export default {
  name: 'HomeBaseCollectionReceipts',
  components: { ErrorState, AlertBanner },
  setup() {
    const {
      collections,
      loading,
      error,
      params,
      fetchCollections,
      exportToCSV
    } = useHomebaseCollectionReceipts()

    const filters = reactive({
      status: 'any',
      householdId: '',
      assignedTo: '',
      dateFrom: '',
      dateTo: ''
    })

    const applyFilters = () => {
      params.value.status = filters.status === 'any' ? 'any' : filters.status
      params.value.householdId = filters.householdId.trim()
      params.value.assignedTo = filters.assignedTo.trim()
      params.value.dateFrom = filters.dateFrom
      params.value.dateTo = filters.dateTo
      fetchCollections()
    }

    const reload = () => {
      applyFilters()
    }

    const sortState = reactive({ key: 'requestedAt', dir: 'desc' })
    const sortBy = (key) => {
      if (sortState.key === key) {
        sortState.dir = sortState.dir === 'asc' ? 'desc' : 'asc'
      } else {
        sortState.key = key
        sortState.dir = 'asc'
      }
      // The composable's deep params watcher refetches — no explicit call,
      // otherwise every header click fires two identical requests.
      params.value.sortBy = key
      params.value.sortDir = sortState.dir
    }

    const ariaSort = (key) =>
      sortState.key === key ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : 'none'

    const sortedAndFiltered = computed(() => {
      return collections.value
    })

    // The API truncates at params.limit, so footer totals only cover this page.
    const limitReached = computed(() => collections.value.length >= (params.value.limit || 100))

    const totalVolume = computed(() => {
      return collections.value.reduce((sum, collection) => {
        return sum + (parseFloat(collection.volumeL) || 0)
      }, 0).toFixed(1)
    })

    onMounted(() => {
      fetchCollections()
    })

    return {
      collections,
      loading,
      error,
      filters,
      reload,
      sortedAndFiltered,
      sortBy,
      sortState,
      ariaSort,
      limitReached,
      totalVolume,
      exportToCSV
    }
  }
}
</script>
